import type { QueryClient } from "@tanstack/react-query";
import { type LoaderFunction, redirect } from "react-router";

import { orgStorage } from "@/shared/auth";
import { AgentService } from "@/shared/domain/agents";
import { agentQueryOptions } from "@/shared/domain/agents/hooks";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation";

/** The agent service the app's own loaders read through. */
export async function makeAgentService(): Promise<AgentService> {
  return new AgentService(await makeAuthenticatedHttpClient());
}

export interface AgentLoaderOptions {
  /**
   * Whether the navigation waits for the agent. The edit form must be in the
   * DOM when the navigation completes for a `#section` hash to scroll to it,
   * so its route waits; the detail page only primes the cache, because the app
   * has no navigation-pending UI and waiting would freeze the click that got
   * here for a round trip.
   */
  blocking: boolean;
}

/**
 * A loader for the routes under `/agents/:agentId` that puts the agent into
 * the query cache under the key `useAgent` reads, so the page renders with it
 * on its first paint.
 *
 * It never fails: a loader error lands on the app's root error page and
 * replaces the whole app, so a failed read resolves to `null` and the page's
 * own `useAgent` reports it inside the page's chrome instead.
 */
export function makeAgentLoader(
  queryClient: QueryClient,
  makeService: () => Promise<AgentService>,
  { blocking }: AgentLoaderOptions,
): LoaderFunction {
  return async function loadAgent({ params }) {
    const { agentId } = params;
    const org = orgStorage.getCurrentOrg();
    if (!org) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }
    if (agentId === undefined) {
      return null;
    }

    const options = async () =>
      agentQueryOptions(agentId, org.org_id, await makeService());

    if (!blocking) {
      // A prefetch settles on its own and never rejects; only making the
      // service can, and the page's own read reports that too.
      void options()
        .then((query) => queryClient.prefetchQuery(query))
        .catch(() => undefined);
      return null;
    }

    try {
      await queryClient.ensureQueryData(await options());
    } catch {
      // The page's own read reports it; see above.
    }
    return null;
  };
}
